export type CodeLang = "c" | "java" | "python";

import SHARED_ATTENDANCE from "./shared-attendance-slugs.json";

/**
 * 경로 → 콘텐츠 slug (leading slash·anchor 제거, `attendance_records.slug` 와 같은 형식).
 * `attendanceSlugOf`(study-plan-state)가 이 함수를 그대로 쓴다 — 출석 키와 아래 공유 판정이
 * 같은 정규화를 쓰게 해서, 한쪽만 규칙이 바뀌어 판정과 키가 어긋나는 일을 막는다.
 */
export function contentSlugOf(path: string): string {
  return path.split("#")[0].replace(/^\//, "");
}

/**
 * 출석을 언어별로 나누지 않는 페이지. (JCG-3655)
 *
 * 하나의 개념을 언어별로 나눠 설명하는 게 아니라 "세 언어를 나란히 비교"하는 페이지는
 * 한 번 읽으면 세 언어를 다 보게 된다. 언어별 출석(JCG-2658)을 적용하면 탭을 바꿔도 출석이
 * 옮겨가지 않아 "C로 봤는데 Python 출석으로 고정돼 있다"처럼 보인다.
 *
 * `shared-attendance-slugs.json` 이 이 사실의 **단일 출처**다. 세 곳이 같은 파일을 본다.
 *   - 출석 UI: `AttendanceFloatingButton` 이 언어 칩 없이 단일 출석(codeLang='')으로 동작
 *   - 학습계획 인덱스: `build-study-plan-index.mjs` 가 언어 태깅을 생략
 *   - 기존 개인 사본: `studyTaskCodeLang` 이 매칭 단계에서 언어를 지움 — 이미 저장된 사본에는
 *     c/java/python 태그가 남아 있어 MDX·인덱스를 고쳐도 사라지지 않기 때문
 *
 * DB 쪽 `attendance_default_code_lang` 의 목록에서도 빠져 있어야 정규화 trigger 가 '' 를
 * 되돌리지 않는다. 그 정합성은 `tests/lib/sharedAttendanceSlugs.test.ts` 가 검증한다.
 */
const SHARED_ATTENDANCE_SLUGS: ReadonlySet<string> = new Set(
  SHARED_ATTENDANCE.slugs,
);

/**
 * 현재 학습 페이지는 경로만 넘기고, 과거 계획을 읽는 경로는 반드시 회차도 넘긴다.
 * 도입 회차 이후가 나중에 동결되어도 통합 출석을 유지하도록 고정된 도입 경계를 쓴다.
 */
export function isSharedAttendanceSlug(path: string, examSessionId?: string): boolean {
  if (!SHARED_ATTENDANCE_SLUGS.has(contentSlugOf(path))) return false;
  if (examSessionId === undefined) return true;
  const [year, round] = examSessionId.split("-").map(Number);
  const [fromYear, fromRound] = SHARED_ATTENDANCE.fromExamSessionId.split("-").map(Number);
  return year > fromYear || (year === fromYear && round >= fromRound);
}

const TRAIL_CODE_LANGS: Record<string, CodeLang> = {
  "/coding/c-language": "c",
  "/coding/java": "java",
  "/coding/python": "python",
};

/**
 * 공통 코딩 글을 어느 과목 지도에서 열었는지 언어로 변환한다.
 * 직접 방문처럼 trail 문맥이 없거나 SQL 등 코드 언어가 아닌 지도면 null을 반환한다.
 */
export function getCodeLangFromTrailSearch(search: string): CodeLang | null {
  const trail = new URLSearchParams(search).get("trail")?.replace(/\/+$/, "");
  return trail ? (TRAIL_CODE_LANGS[trail] ?? null) : null;
}
